import React, { useCallback } from "react";
import {
  Table,
  TableInput,
  TableSelect,
  Checkbox,
} from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import { cents, currencyToCents } from "byzantine";
import filters from "byzantine/src/filters";
import type { WirePaymentEdit, WirePaymentErrors } from "./useWirePaymentsTable";
import WireSelectedRecipientCard from "./WireSelectedRecipientCard";
import WireRecipientSelector from "./WireRecipientSelector";
import styles from "./WirePaymentsTableScreen.module.scss";

type WirePaymentRowProps = {
  index: number;
  rowNumber: number;
  recipient: API.Recipient | null;
  recipients: API.Recipient[];
  edit: WirePaymentEdit;
  errors: WirePaymentErrors | undefined;
  wireReasons: string[];
  hasWireReasons: boolean;
  isSelected: boolean;
  onFieldChange: (
    idx: number,
    field: keyof WirePaymentEdit,
    value: string | number
  ) => void;
  onSelectRecipient: (rowIndex: number, recipient: API.Recipient) => void;
  onEditRecipient: (
    recipientId: API.RecipientId,
    rowIndex: number
  ) => void;
  onAddRecipient: (rowIndex: number) => void;
  onClearRecipient: (rowIndex: number) => void;
  onClickUnsavedRecipient: (recipient: API.Recipient, rowIndex: number) => void;
  onToggleSelection: (idx: number) => void;
};

const WirePaymentRow = React.memo(
  ({
    index,
    rowNumber,
    recipient,
    recipients,
    edit,
    errors,
    wireReasons,
    hasWireReasons,
    isSelected,
    onFieldChange,
    onSelectRecipient,
    onEditRecipient,
    onAddRecipient,
    onClearRecipient,
    onClickUnsavedRecipient,
    onToggleSelection,
  }: WirePaymentRowProps) => {
    const { l10n } = useLocalization();

    const handleWireReasonChange = useCallback(
      (value: string) => {
        onFieldChange(index, "wire_reason", value);
      },
      [index, onFieldChange]
    );

    const handleMemoChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onFieldChange(index, "memo", e.target.value);
      },
      [index, onFieldChange]
    );

    const handleAmountChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = currencyToCents(e.target.value);
        onFieldChange(index, "amount", value);
      },
      [index, onFieldChange]
    );

    const displayAmount = edit.amount
      ? filters.currency(cents(edit.amount))
      : "";

    return (
      <Table.Row>
        <Table.Cell>
          <span className={styles.rowNumber}>{rowNumber}</span>
        </Table.Cell>
        <Table.Cell>
          <Checkbox
            onChange={() => onToggleSelection(index)}
            checked={isSelected}
          />
        </Table.Cell>
        <Table.Cell>
          {recipient?.id ? (
            <WireSelectedRecipientCard
              recipient={recipient}
              rowIndex={index}
              onEditRecipient={onEditRecipient}
              onClearRecipient={onClearRecipient}
              handleClickUnsavedRecipient={onClickUnsavedRecipient}
            />
          ) : (
            <WireRecipientSelector
              rowIndex={index}
              recipients={recipients}
              onSelectRecipient={onSelectRecipient}
              onAddRecipient={onAddRecipient}
              onEditRecipient={onEditRecipient}
              error={Boolean(errors?.recipient)}
            />
          )}
        </Table.Cell>
        {hasWireReasons && (
          <Table.Cell>
            <TableSelect
              id={`wire-reason-${rowNumber}`}
              label={l10n.getString("wire-payments-table-col-reason")}
              value={edit.wire_reason}
              onChange={handleWireReasonChange}
              hasError={Boolean(errors?.wire_reason)}
            >
              {wireReasons.map((reason) => (
                <TableSelect.Item key={reason} value={reason}>
                  {reason}
                </TableSelect.Item>
              ))}
            </TableSelect>
          </Table.Cell>
        )}
        <Table.Cell>
          <TableInput
            label={l10n.getString("wire-payments-table-col-memo")}
            placeholder={l10n.getString(
              "wire-payments-table-placeholder-memo"
            )}
            maxLength={140}
            hasError={Boolean(errors?.memo)}
            value={edit.memo}
            onChange={handleMemoChange}
          />
        </Table.Cell>
        <Table.Cell>
          <div className={styles.rightAligned}>
            <TableInput
              label={l10n.getString("wire-payments-table-col-amount")}
              placeholder={l10n.getString(
                "wire-payments-table-placeholder-amount"
              )}
              hasError={Boolean(errors?.amount)}
              value={displayAmount}
              onChange={handleAmountChange}
            />
          </div>
        </Table.Cell>
      </Table.Row>
    );
  }
);

WirePaymentRow.displayName = "WirePaymentRow";

export default WirePaymentRow;
